import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { adminApi } from "@/api/admin";

export const Route = createFileRoute("/admin/payments")({
  component: AdminPaymentsPage,
});

function AdminPaymentsPage() {
  const [status, setStatus] = useState<string>("");
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["admin-payments-all", status],
    queryFn: () => adminApi.listPayments({ status: status || undefined, limit: 500 }),
  });
  const refund = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => adminApi.refundPayment(id, reason),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-payments-all"] }),
  });
  // Cancel = reject a pending (unpaid) payment — the admin equivalent of the
  // customer's cancel button.
  const cancel = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) => adminApi.rejectPayment(id, reason),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-payments-all"] }),
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Payments</h1>
        <p className="mt-1 text-ink/65">All payments across all projects.</p>
      </div>

      <div className="flex gap-3">
        <select className="input w-44" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All statuses</option>
          <option value="pending">Pending</option>
          <option value="confirmed">Confirmed</option>
          <option value="refunded">Refunded</option>
          <option value="rejected">Rejected</option>
        </select>
      </div>

      <div className="card overflow-x-auto p-0">
        <table className="w-full text-sm">
          <thead className="bg-cream text-left text-xs uppercase tracking-wide text-ink/55">
            <tr>
              <th className="px-4 py-3">Received</th>
              <th className="px-4 py-3">Project</th>
              <th className="px-4 py-3">Amount</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Tx</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? <tr><td colSpan={6} className="px-4 py-6 text-ink/55">Loading…</td></tr>
            : data?.payments.length ? data.payments.map((p) => (
              <tr key={p.id} className="border-t border-ink/8">
                <td className="px-4 py-3">{new Date(p.received_at).toLocaleString()}</td>
                <td className="px-4 py-3">{p.project_name ?? <code className="font-mono text-xs">{p.project_id.slice(0, 8)}…</code>}</td>
                <td className="px-4 py-3 font-medium">${p.amount_usdc}</td>
                <td className="px-4 py-3">{p.status}</td>
                <td className="px-4 py-3 font-mono text-xs">{p.tx_signature?.slice(0, 8) ?? "—"}…</td>
                <td className="px-4 py-3 text-right">
                  {p.status === "confirmed" && (
                    <button
                      onClick={() => {
                        const reason = prompt("Refund reason:");
                        if (reason !== null) refund.mutate({ id: p.id, reason });
                      }}
                      className="text-sm text-red-600 hover:text-red-800"
                    >Refund</button>
                  )}
                  {p.status === "pending" && (
                    <button
                      onClick={() => {
                        if (confirm("Cancel this pending payment?")) {
                          const reason = prompt("Cancellation reason (optional):") ?? undefined;
                          cancel.mutate({ id: p.id, reason: reason || undefined });
                        }
                      }}
                      disabled={cancel.isPending}
                      className="text-sm text-red-600 hover:text-red-800 disabled:opacity-50"
                    >Cancel</button>
                  )}
                </td>
              </tr>
            )) : <tr><td colSpan={6} className="px-4 py-6 text-ink/55">No payments.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
